/* Firebase Performance Monitoring for the lego player — and ONLY for it.
   Mirrors utils/datadog.js:
   - The SDK is a LAZY split chunk (dynamic import of firebase/app +
     firebase/performance), fetched only when VITE_FIREBASE_PERF_ACTIVE is on.
     The lego route shell starts it (in parallel with the player chunk) the
     first time a player mounts; nothing else in the app loads it.
   - Only the lego's custom traces are reported. The SDK's automatic
     page-load / network-request instrumentation is OFF, and the page's
     `performance.measure()` entries are hidden from it (see
     withoutPageTimings) — those used to flood the console with GTM's own
     measures (`GTM-G-…:180:17:14`) and browser extensions' (`Wappalyzer:
     getDom`), one trace name per measure.
   - Fails OPEN: any init error logs and disables (handle stays undefined), so
     telemetry can never break playback.
   The handle it produces has the LegoPerfHandle / LegoTrace shape declared in
   src/lego-player/player/LegoPlayer.tsx (the solid mount contract) — the
   player optional-chains every call, so an undefined handle is a no-op.

   The config values (from the boss's Firebase project) must live in env vars,
   NOT in this file; the pasted object pointed at the `dtvo-clientapps-test`
   project — wire a real prod project instead. */

/* One handle per page (like the shared NPAW plugin): the SDK is a page-level
   singleton; re-initializing per lego mount would re-register the app/analsis
   and duplicate traces. */
let handle;
let initPromise;

function buildConfig() {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };
}

/* Trace calls throw on invalid input (e.g. an empty attribute value throws
   `performance/invalid attribute value`). The lego calls them from its mount
   path, so a throw there stopped playback before the authorizer request. */
function failOpen(call) {
  try {
    call();
  } catch (error) {
    console.error('[firebasePerf] trace call failed:', error);
  }
}

/* Page timings the SDK must not see: 'resource' feeds the automatic
   network-request traces, 'measure' the user-timing ones. */
const PAGE_TIMING_ENTRY_TYPES = new Set(['resource', 'measure']);

/* The SDK turns EVERY `performance.measure()` on the page into a custom trace
   named after the measure, and it creates those with isAuto=false, so
   `instrumentationEnabled: false` does NOT stop them (it only drops page-load
   and network traces). Its internal Api captures `window.performance` and
   `window.PerformanceObserver` once, synchronously inside
   initializePerformance(); so for exactly that call it gets a view without
   page timings: no PerformanceObserver (the SDK only observes 'resource' and
   'measure' with it) and no buffered entries of those types. The lego's own
   traces still work: their start/stop marks and the measure read-back go to
   the real performance object. The globals are restored before anything else
   can run. */
function withoutPageTimings(init) {
  const realPerformance = window.performance;
  const performanceView = {
    mark: (...args) => realPerformance.mark(...args),
    measure: (...args) => realPerformance.measure(...args),
    getEntriesByName: (...args) => realPerformance.getEntriesByName(...args),
    getEntriesByType: (type) => (
      PAGE_TIMING_ENTRY_TYPES.has(type) ? [] : realPerformance.getEntriesByType(type)
    ),
    now: () => realPerformance.now(),
    timeOrigin: realPerformance.timeOrigin,
    timing: realPerformance.timing,
  };
  const saved = ['performance', 'PerformanceObserver'].map((key) => [
    key,
    Object.getOwnPropertyDescriptor(window, key),
  ]);
  try {
    Object.defineProperty(window, 'performance', {
      configurable: true, writable: true, value: performanceView,
    });
    Object.defineProperty(window, 'PerformanceObserver', {
      configurable: true, writable: true, value: undefined,
    });
    return init();
  } finally {
    for (const [key, descriptor] of saved) {
      if (descriptor) {
        Object.defineProperty(window, key, descriptor);
      } else {
        delete window[key];
      }
    }
  }
}

/* Starts (and caches) the init. Returns the same promise to every caller so
   the lego shell can await a warm init without starting it twice. Resolves
   `undefined` when the flag is off or init fails. Called by the lego route
   shell only (routes/Private/LegoPlayer). */
export function initLegoFirebasePerf() {
  if (import.meta.env.VITE_FIREBASE_PERF_ACTIVE !== 'true') {
    return Promise.resolve(undefined);
  }
  if (handle !== undefined) return Promise.resolve(handle);
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const [{ initializeApp }, { initializePerformance, trace }] = await Promise.all([
          import('firebase/app'),
          import('firebase/performance'),
        ]);
        const app = initializeApp(buildConfig());
        const perf = withoutPageTimings(
          () => initializePerformance(app, { instrumentationEnabled: false }),
        );
        handle = {
          trace: (name) => {
            const t = trace(perf, name);
            return {
              start: () => failOpen(() => t.start()),
              stop: () => failOpen(() => t.stop()),
              putMetric: (metricName, value) => failOpen(() => t.putMetric(metricName, value)),
              putAttribute: (attributeName, value) => failOpen(
                () => t.putAttribute(attributeName, value),
              ),
            };
          },
        };
      } catch (error) {
        /* Fail open: telemetry is never allowed to break playback. */
        console.error('[firebasePerf] disabled (init failed):', error);
        handle = undefined;
      }
      return handle;
    })();
  }
  return initPromise;
}
